/**
 * Lifecycle event nodes — entry points for graph execution.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowOut, output } from '@engine/redprint';

export const OnAwake: NodeBehaviour = {
  id: 'events.awake',
  name: 'On Awake',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'Zap',
  description: 'Called once when the entity is first activated.',
  keywords: ['start', 'init', 'begin'],
  inputs: [],
  outputs: [flowOut('exec_out', '▶')],
};

export const OnStart: NodeBehaviour = {
  id: 'events.start',
  name: 'On Start',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'Play',
  description: 'Called once on the first frame after Awake.',
  keywords: ['begin', 'ready'],
  inputs: [],
  outputs: [flowOut('exec_out', '▶')],
};

export const OnUpdate: NodeBehaviour = {
  id: 'events.update',
  name: 'On Update',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'RefreshCw',
  description: 'Called every frame.',
  keywords: ['tick', 'frame', 'loop'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('dt', 'Delta Time', 'f32'),
  ],
  execute(ctx) {
    ctx.setOutput('dt', ctx.deltaTime);
  },
};

export const OnFixedUpdate: NodeBehaviour = {
  id: 'events.fixedUpdate',
  name: 'On Fixed Update',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'Clock',
  description: 'Called at fixed time intervals (physics step).',
  keywords: ['physics', 'step'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('dt', 'Fixed Delta', 'f32'),
  ],
};

export const OnDestroy: NodeBehaviour = {
  id: 'events.destroy',
  name: 'On Destroy',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'Trash2',
  description: 'Called when the entity is about to be destroyed.',
  keywords: ['remove', 'cleanup', 'dispose'],
  inputs: [],
  outputs: [flowOut('exec_out', '▶')],
};

export const OnEnable: NodeBehaviour = {
  id: 'events.enable',
  name: 'On Enable',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'Eye',
  description: 'Called when the component is enabled.',
  inputs: [],
  outputs: [flowOut('exec_out', '▶')],
};

export const OnDisable: NodeBehaviour = {
  id: 'events.disable',
  name: 'On Disable',
  category: 'Events',
  isEvent: true,
  color: '#e74c3c',
  icon: 'EyeOff',
  description: 'Called when the component is disabled.',
  inputs: [],
  outputs: [flowOut('exec_out', '▶')],
};

export const OnCollisionEnter: NodeBehaviour = {
  id: 'events.collisionEnter',
  name: 'On Collision Enter',
  category: 'Events',
  isEvent: true,
  color: '#e67e22',
  icon: 'Zap',
  description: 'Called when this entity starts colliding with another.',
  keywords: ['hit', 'touch', 'physics'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('other', 'Other Entity', 'entity'),
  ],
};

export const OnCollisionExit: NodeBehaviour = {
  id: 'events.collisionExit',
  name: 'On Collision Exit',
  category: 'Events',
  isEvent: true,
  color: '#e67e22',
  icon: 'ZapOff',
  description: 'Called when collision with another entity ends.',
  keywords: ['separate', 'physics'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('other', 'Other Entity', 'entity'),
  ],
};

export const OnCollisionStay: NodeBehaviour = {
  id: 'events.collisionStay',
  name: 'On Collision Stay',
  category: 'Events',
  isEvent: true,
  color: '#e67e22',
  icon: 'Zap',
  description: 'Called every frame while colliding with another entity.',
  keywords: ['contact', 'touching', 'physics'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('other', 'Other Entity', 'entity'),
    output('contactX', 'Contact X', 'f32'),
    output('contactY', 'Contact Y', 'f32'),
    output('normalX', 'Normal X', 'f32'),
    output('normalY', 'Normal Y', 'f32'),
  ],
};

export const OnTriggerEnter: NodeBehaviour = {
  id: 'events.triggerEnter',
  name: 'On Trigger Enter',
  category: 'Events',
  isEvent: true,
  color: '#9b59b6',
  icon: 'LogIn',
  description: 'Called when an entity enters a trigger volume.',
  keywords: ['overlap', 'sensor', 'zone'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('other', 'Other Entity', 'entity'),
  ],
};

export const OnTriggerStay: NodeBehaviour = {
  id: 'events.triggerStay',
  name: 'On Trigger Stay',
  category: 'Events',
  isEvent: true,
  color: '#9b59b6',
  icon: 'SquareStack',
  description: 'Called every frame while an entity is inside a trigger.',
  keywords: ['overlap', 'inside', 'zone'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('other', 'Other Entity', 'entity'),
  ],
};

export const OnTriggerExit: NodeBehaviour = {
  id: 'events.triggerExit',
  name: 'On Trigger Exit',
  category: 'Events',
  isEvent: true,
  color: '#9b59b6',
  icon: 'LogOut',
  description: 'Called when an entity leaves a trigger volume.',
  keywords: ['overlap', 'exit', 'zone'],
  inputs: [],
  outputs: [
    flowOut('exec_out', '▶'),
    output('other', 'Other Entity', 'entity'),
  ],
};

export const LIFECYCLE_NODES: readonly NodeBehaviour[] = [
  OnAwake, OnStart, OnUpdate, OnFixedUpdate, OnDestroy,
  OnEnable, OnDisable,
  OnCollisionEnter, OnCollisionStay, OnCollisionExit,
  OnTriggerEnter, OnTriggerStay, OnTriggerExit,
];
